/**
 * TemplateUpload
 * Allows organizer to upload a Canva PDF as a presentation template.
 * Renders PDF pages via PDF.js as canvas elements.
 * Saves templatePdfUrl to meetingSettings/{entityId}.
 *
 * Rule 2: Explicit types — no any.
 * Rule 5: uploadBytesResumable with progress.
 * Rule 6: Separate component < 300 lines.
 * Rule 9: Constants from meetingConstants.
 * Rule 10: TemplateRenderState discriminated union.
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { storage, db } from '../../../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { COLLECTIONS, STORAGE_PATHS, STRINGS, MAX_TEMPLATE_PDF_BYTES } from '../meetingConstants';
import { loadPdfDocumentFromUrl } from '../loadPdfDocumentFromUrl';
import type { TemplateRenderState } from '../networkClubTypes';

interface TemplateUploadProps {
  entityId: string;
  currentTemplateUrl?: string | null;
  isHost: boolean;
  /** Called with the URL after successful upload */
  onUploaded?: (url: string) => void;
  /** `dark` = wit op donkere achtergrond (standaard); `light` = hub-kaart stijl */
  surface?: 'dark' | 'light';
  /** Club-accent (hex), o.a. voortgangsbalk bij surface light */
  accentColor?: string;
}

const MAX_TEMPLATE_PDF_MB = Math.round(MAX_TEMPLATE_PDF_BYTES / 1024 / 1024);

const TemplateUpload: React.FC<TemplateUploadProps> = ({
  entityId,
  currentTemplateUrl,
  isHost,
  onUploaded,
  surface = 'dark',
  accentColor = '#64748b',
}) => {
  const light = surface === 'light';
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [renderState, setRenderState] = useState<TemplateRenderState>({ status: 'idle' });
  const [currentPage, setCurrentPage] = useState(1);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastLoadedUrlRef = useRef<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdfDocRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdfRenderTaskRef = useRef<{ cancel: (extraDelay?: number) => void } | null>(null);

  useEffect(() => () => {
    pdfRenderTaskRef.current?.cancel();
    pdfRenderTaskRef.current = null;
  }, []);

  // ── Load existing template ───────────────────────────────────────────────
  const renderPage = useCallback(async (pageNum: number): Promise<void> => {
    if (!pdfDocRef.current || !canvasRef.current) return;
    pdfRenderTaskRef.current?.cancel();
    pdfRenderTaskRef.current = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let task: any = null;
    try {
      const page = await pdfDocRef.current.getPage(pageNum);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const viewport = page.getViewport({ scale: 1.2 });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const bg = light ? '#f8fafc' : '#0f172a';
      task = page.render({ canvas, viewport, background: bg });
      pdfRenderTaskRef.current = task;
      await task.promise;
    } catch (err: unknown) {
      const e = err instanceof Error ? err : null;
      if (e?.name === 'RenderingCancelledException' || /Rendering cancelled/i.test(e?.message ?? '')) {
        return;
      }
      console.error('[TemplateUpload] Render page error:', err);
      setRenderState({ status: 'error', message: STRINGS.template.renderError });
    } finally {
      if (task && pdfRenderTaskRef.current === task) {
        pdfRenderTaskRef.current = null;
      }
    }
  }, [light]);

  const loadPdf = useCallback(async (url: string): Promise<void> => {
    pdfRenderTaskRef.current?.cancel();
    pdfRenderTaskRef.current = null;
    pdfDocRef.current?.destroy?.();
    pdfDocRef.current = null;
    setRenderState({ status: 'loading', progress: 0 });
    try {
      const pdfDoc = await loadPdfDocumentFromUrl(url, (pct) => {
        setRenderState({ status: 'loading', progress: pct });
      });
      pdfDocRef.current = pdfDoc;
      setRenderState({ status: 'ready', pageCount: pdfDoc.numPages });
      /* Geen renderPage hier: canvas staat pas na commit in de DOM (was: lege preview tot pagina-wissel). */
    } catch (err) {
      console.error('[TemplateUpload] PDF load error:', err);
      setRenderState({ status: 'error', message: STRINGS.template.renderError });
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    if (!currentTemplateUrl) {
      pdfRenderTaskRef.current?.cancel();
      pdfRenderTaskRef.current = null;
      lastLoadedUrlRef.current = null;
      pdfDocRef.current?.destroy?.();
      pdfDocRef.current = null;
      setRenderState({ status: 'idle' });
      return;
    }
    if (lastLoadedUrlRef.current === currentTemplateUrl) return;
    lastLoadedUrlRef.current = currentTemplateUrl;
    void loadPdf(currentTemplateUrl);
  }, [currentTemplateUrl, loadPdf]);

  const readyPageCount = renderState.status === 'ready' ? renderState.pageCount : 0;
  useLayoutEffect(() => {
    if (renderState.status !== 'ready') return;
    let cancelled = false;
    const paint = () => {
      if (!cancelled) void renderPage(currentPage);
    };
    let rafInner = 0;
    const rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(paint);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
    };
  }, [renderState.status, readyPageCount, currentPage, renderPage]);

  // ── Upload ──────────────────────────────────────────────────────────────
  const handleFileChange = async (file: File): Promise<void> => {
    if (file.type !== 'application/pdf') {
      setUploadError('Alleen PDF-bestanden zijn toegestaan.');
      return;
    }
    if (file.size > MAX_TEMPLATE_PDF_BYTES) {
      setUploadError(STRINGS.template.pdfTooLarge(MAX_TEMPLATE_PDF_MB));
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const path = STORAGE_PATHS.templatePdf(entityId);
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);

      const downloadUrl = await new Promise<string>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snap) => {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setUploadProgress(pct);
          },
          (err) => reject(err),
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          },
        );
      });

      // Persist URL to meetingSettings
      await setDoc(
        doc(db, COLLECTIONS.SETTINGS, entityId),
        { templatePdfUrl: downloadUrl, updatedAt: serverTimestamp() },
        { merge: true },
      );

      onUploaded?.(downloadUrl);
      /* Preview: loadPdf via useEffect op nieuwe currentTemplateUrl (geen dubbele load). */
    } catch (err) {
      console.error('[TemplateUpload] Upload failed:', err);
      setUploadError(STRINGS.errors.generic);
    } finally {
      setUploading(false);
    }
  };

  const pageCount = renderState.status === 'ready' ? renderState.pageCount : 0;

  return (
    <div className="space-y-3">
      {/* Upload controls — host only */}
      {isHost && (
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFileChange(f);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            aria-label={STRINGS.template.uploadLabel}
            className={
              light
                ? 'min-h-[44px] px-4 rounded-xl text-xs font-semibold border border-slate-200 bg-white text-slate-800 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-50'
                : 'min-h-[44px] px-4 rounded-xl text-xs font-semibold border border-white/20 bg-white/10 hover:bg-white/20 text-white transition-all disabled:opacity-50'
            }
          >
            {uploading
              ? `Uploaden ${uploadProgress}%`
              : currentTemplateUrl
              ? 'Template vervangen (PDF)'
              : STRINGS.template.uploadLabel}
          </button>

          {uploading && (
            <div className={light ? 'h-1.5 rounded-full bg-slate-100 overflow-hidden' : 'h-1.5 rounded-full bg-white/15 overflow-hidden'}>
              <div
                className={light ? 'h-full rounded-full transition-all' : 'h-full rounded-full bg-orange-400 transition-all'}
                style={light ? { width: `${uploadProgress}%`, backgroundColor: accentColor } : { width: `${uploadProgress}%` }}
              />
            </div>
          )}
          {uploadError && (
            <p role="alert" className={light ? 'text-[11px] text-rose-600' : 'text-[11px] text-rose-400'}>{uploadError}</p>
          )}
        </div>
      )}

      {/* PDF preview */}
      {renderState.status === 'loading' && (
        <p className={light ? 'text-xs text-slate-500' : 'text-xs text-white/60'}>Template laden… {renderState.progress}%</p>
      )}
      {renderState.status === 'error' && (
        <p role="alert" className={light ? 'text-xs text-rose-600' : 'text-xs text-rose-400'}>{renderState.message}</p>
      )}
      {renderState.status === 'idle' && !currentTemplateUrl && (
        <p className={light ? 'text-xs text-slate-400 italic' : 'text-xs text-white/40 italic'}>{STRINGS.template.noTemplate}</p>
      )}
      {renderState.status === 'ready' && (
        <div className="space-y-2">
          <canvas
            ref={canvasRef}
            className={light ? 'w-full rounded-xl border border-slate-200 bg-white shadow-sm' : 'w-full rounded-xl border border-white/15'}
            aria-label="PDF template preview"
          />
          {pageCount > 1 && (
            <div className="flex items-center gap-2 justify-center">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Vorige pagina"
                className={
                  light
                    ? 'min-h-[36px] px-3 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40'
                    : 'min-h-[36px] px-3 rounded-lg border border-white/20 text-xs text-white disabled:opacity-40'
                }
              >
                ←
              </button>
              <span className={light ? 'text-xs text-slate-500' : 'text-xs text-white/60'}>{currentPage} / {pageCount}</span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
                disabled={currentPage === pageCount}
                aria-label="Volgende pagina"
                className={
                  light
                    ? 'min-h-[36px] px-3 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40'
                    : 'min-h-[36px] px-3 rounded-lg border border-white/20 text-xs text-white disabled:opacity-40'
                }
              >
                →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateUpload;
